// The app's analytics seam, on the shared @libs/rudderstack entries via the
// Metro platform split in ./entry (native build → /native, web build →
// /browser). This module keeps the app-side concerns: the capability gate,
// Firebase uid resolution, error reporting, and the LIB → app session stitch
// (an identity concern that deliberately stays out of the lib).
//
// Event names/payloads live in the shared catalog (libs/rudderstack
// core/events.ts); `track` is generic over it, so a typo'd event or drifted
// payload is a build error.

import * as analytics from "./entry";
import { EventMap, EventNames } from "@utils/rudderstack/types";
import { IdentifyProps } from "@utils/analytics.types";
import { useBranchStore } from "@utils/branch/store";
import { auth } from "@utils/firebase";
import { captureException } from "@utils/captureException";
import { safeAsyncStorage } from "@utils/safeAsyncStorage";
import { capabilities } from "../../capabilities";
import config from "@utils/config";

const isRudderStackAllowed = () => capabilities.allowed("rudderstack");

// Session attribution (the link-tracking cid slot — LIS-110). Once a cid is
// set, every subsequent event carries `session_cid`.
export const { getSessionAttribution, setSessionAttribution } = analytics;

export type LibStitchSource = "branch" | "universal" | "scheme";

/**
 * Persisted state keys for the LIB → app session stitch.
 *
 * - STITCHED_AIDS: JSON array of `lsn_aid`s we have already fired
 *   `lib_session_linked` for. Survives process restarts so cold-start Branch
 *   fallback polling (which re-delivers the same params) cannot double-fire.
 * - PENDING_STITCH: a single `{ lsn_aid, source }` adopted on a fresh launch
 *   while no `uid` was present yet. AppLayout flushes (bridges) it once the
 *   user authenticates so the web session merges into the uid cluster.
 * - FIRST_LAUNCH_SEEN: set the first time a stitch is processed on this
 *   install. Its ABSENCE is our heuristic for "genuinely fresh launch":
 *   - absent + no uid  -> adopt `lsn_aid` as the app anonymous id.
 *   - present + no uid -> the app already has anonymous history, so we only
 *     emit a property-only `lib_session_linked` (BigQuery joins on lsn_aid;
 *     Mixpanel merges at next login via the pending bridge / identify).
 *   Heuristic caveat: an install that predates this feature has no flag, so
 *   its first lsn_aid arrival is treated as "fresh" and would adopt. That is
 *   acceptable — adopting only overwrites the device anonymous id (never the
 *   uid), and the carried lsn_aid still merges into the uid at next login.
 */
const STITCH_STORAGE_KEYS = {
  STITCHED_AIDS: "lsn:lib_stitched_aids",
  PENDING_STITCH: "lsn:lib_pending_stitch",
  FIRST_LAUNCH_SEEN: "lsn:lib_first_launch_seen",
} as const;

// In-memory dedup so repeated calls within a single session (e.g. Branch
// subscribe + fallback polling firing in the same process) no-op without a
// disk round-trip. Backed by the persisted STITCHED_AIDS for cold starts.
const firedStitches = new Set<string>();

const readStitchedAids = async (): Promise<string[]> => {
  try {
    const raw = await safeAsyncStorage.getItem(STITCH_STORAGE_KEYS.STITCHED_AIDS);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
};

const persistStitchedAid = async (lsn_aid: string) => {
  try {
    const existing = await readStitchedAids();
    if (existing.includes(lsn_aid)) return;
    // Cap retained entries to avoid unbounded growth.
    const next = [...existing, lsn_aid].slice(-32);
    await safeAsyncStorage.setItem(
      STITCH_STORAGE_KEYS.STITCHED_AIDS,
      JSON.stringify(next),
    );
  } catch (error) {
    captureException(error, {
      channel: "breadcrumb",
      area: "analytics",
      operation: "rudderstack-persist-stitched-aid",
    });
  }
};

export type PendingLibStitch = { lsn_aid: string; source: LibStitchSource };

const readPendingStitch = async (): Promise<PendingLibStitch | null> => {
  try {
    const raw = await safeAsyncStorage.getItem(
      STITCH_STORAGE_KEYS.PENDING_STITCH,
    );
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.lsn_aid === "string" &&
      typeof parsed.source === "string"
    ) {
      return parsed as PendingLibStitch;
    }
    return null;
  } catch {
    return null;
  }
};

const writePendingStitch = async (pending: PendingLibStitch) => {
  try {
    await safeAsyncStorage.setItem(
      STITCH_STORAGE_KEYS.PENDING_STITCH,
      JSON.stringify(pending),
    );
  } catch (error) {
    captureException(error, {
      channel: "breadcrumb",
      area: "analytics",
      operation: "rudderstack-write-pending-stitch",
    });
  }
};

const clearPendingStitch = async () => {
  try {
    await safeAsyncStorage.removeItem(STITCH_STORAGE_KEYS.PENDING_STITCH);
  } catch (error) {
    captureException(error, {
      channel: "breadcrumb",
      area: "analytics",
      operation: "rudderstack-clear-pending-stitch",
    });
  }
};

// Routes lib-entry failures into the app's error tracker, matching the
// operation names the old inline try/catches reported.
const reportAnalyticsError = (
  error: unknown,
  context: { operation: string; event?: string },
) => {
  captureException(error, {
    channel: "breadcrumb",
    area: "analytics",
    operation: `rudderstack-${context.operation}`,
    ...(context.event ? { tags: { event: context.event } } : {}),
  });
};

export const initRudderStack = async () => {
  if (!isRudderStackAllowed()) {
    console.log("::rudderstack index:: disabled in preview");
    return;
  }

  console.log("::rudderstack index:: initializing RudderStack");
  await analytics.initAnalytics({
    writeKey: config.RUDDERSTACK.WRITE_KEY,
    dataPlaneUrl: config.RUDDERSTACK.DATA_PLANE_URL,
    projectId: config.PROJECT_ID,
    getUserId: () => auth.currentUser?.uid,
    onError: reportAnalyticsError,
  });
};

export const logout = async () => {
  console.log("::rudderstack logout:: logging out");
  useBranchStore.getState().reset();
  if (!isRudderStackAllowed()) {
    return;
  }

  await analytics.reset();
};

export const identify = async (props: IdentifyProps) => {
  await analytics.identify(props.userId, {
    email: props.email,
    countryCode: props.countryCode,
    ...(props.createdAt === undefined ? {} : { created_at: props.createdAt }),
    plan_name: props.plan_name,
  });
};

export const track = <T extends EventNames>(
  name: T,
  properties: EventMap[T],
) => {
  analytics.track(name, properties);
};

export const screen = (name: string) => {
  analytics.screen(name);
};

export const getAnonymousId = async () => analytics.getAnonymousId();

/**
 * Override the RudderStack anonymous id ($device_id) for future events. No-op
 * in preview where RudderStack is disabled, and for an empty id.
 */
export const setAnonymousId = async (id: string) => {
  if (!id) return;
  await analytics.setAnonymousId(id);
};

/**
 * Track an event against an explicit anonymousId + userId so the call site can
 * guarantee BOTH ride one event (Mixpanel Simplified ID Merge requires this to
 * collapse a $device_id into a $user_id). Awaits the track so the anonymous-id
 * bridge can restore the previous id only after the event has been enqueued.
 */
const trackStitchEvent = async (
  lsn_aid: string,
  source: LibStitchSource,
  uid: string,
) => {
  await analytics.trackAndWait("lib_session_linked", {
    lsn_aid,
    source,
    user_id: uid,
  });
};

/**
 * Stitch a LIB-originated web session into the app journey.
 *
 * `lsn_aid` is the LIB RudderStack anonymousId carried on the "open in app"
 * link. Behaviour (see model in the implementation plan / STITCH_STORAGE_KEYS):
 *
 *  - logged in (uid present)  -> BRIDGE: set anon id to lsn_aid, fire
 *    `lib_session_linked` (carries anonymousId=lsn_aid + user_id=uid so
 *    Mixpanel merges the web $device_id into the uid cluster), then restore the
 *    app's previous anon id.
 *  - fresh launch (no uid, no first-launch flag) -> ADOPT lsn_aid as the app
 *    anon id and persist a pending stitch so the bridge runs at next login.
 *  - anonymous app with history (no uid, first-launch flag present) ->
 *    property-only `lib_session_linked` (BigQuery joins on lsn_aid; Mixpanel
 *    merges at next login).
 *
 * Fires at most once per lsn_aid (in-memory Set + persisted record). Never
 * calls reset()/logout(); never clobbers an existing app anon id except the
 * deliberate fresh-install adopt.
 */
export const applyLibStitch = async ({
  lsn_aid,
  source,
}: {
  lsn_aid: string;
  source: LibStitchSource;
}) => {
  if (!isRudderStackAllowed() || !lsn_aid) {
    return;
  }

  // Fast in-memory dedup. Claim atomically BEFORE any await so two concurrent
  // invocations for the same lsn_aid (e.g. the Branch subscribe callback and a
  // cold-start fallback poll firing in the same tick) cannot both pass this
  // guard (TOCTOU) and double-fire the bridge.
  if (firedStitches.has(lsn_aid)) {
    return;
  }
  firedStitches.add(lsn_aid);

  try {
    // Persisted dedup survives cold-start Branch fallback re-delivery.
    const alreadyStitched = await readStitchedAids();
    if (alreadyStitched.includes(lsn_aid)) {
      return;
    }

    const uid = auth.currentUser?.uid;

    if (uid) {
      // BRIDGE — already logged in. Swap the anon id to lsn_aid and fire the
      // stitch event (carries anonymousId=lsn_aid + user_id=uid). ALWAYS
      // restore the app's previous anon id in a finally — even if the track
      // throws — so a transient failure can't leave future app events
      // attributed to the web lsn_aid. persistStitchedAid only runs on success;
      // a failure propagates to the outer catch, which releases the in-memory
      // claim so the stitch can be retried.
      const prev = await getAnonymousId();
      try {
        await setAnonymousId(lsn_aid);
        await trackStitchEvent(lsn_aid, source, uid);
      } finally {
        if (prev) {
          await setAnonymousId(prev);
        }
      }
      await persistStitchedAid(lsn_aid);
      return;
    }

    // No uid: decide adopt vs property-only on a first-launch heuristic.
    const firstLaunchSeen = await safeAsyncStorage.getItem(
      STITCH_STORAGE_KEYS.FIRST_LAUNCH_SEEN,
    );
    // Mark this install as having seen a stitch from now on.
    await safeAsyncStorage.setItem(STITCH_STORAGE_KEYS.FIRST_LAUNCH_SEEN, "1");

    if (!firstLaunchSeen) {
      // ADOPT on a genuinely fresh launch. Persist a pending stitch so the
      // bridge runs once the user authenticates (AppLayout flushes it).
      await setAnonymousId(lsn_aid);
      await writePendingStitch({ lsn_aid, source });
      await persistStitchedAid(lsn_aid);
      return;
    }

    // Anonymous app with existing history: property-only. BigQuery joins on
    // lsn_aid; Mixpanel merges at next login. Also store as pending so the
    // merge bridge runs deterministically once the user logs in.
    track("lib_session_linked", { lsn_aid, source });
    await writePendingStitch({ lsn_aid, source });
    await persistStitchedAid(lsn_aid);
  } catch (error) {
    // Release the in-memory claim so a transient failure can be retried within
    // this session (the persisted record still guards against a real re-fire).
    firedStitches.delete(lsn_aid);
    captureException(error, {
      channel: "breadcrumb",
      area: "analytics",
      operation: "rudderstack-apply-lib-stitch",
      tags: { source },
    });
  }
};

/**
 * Flush a pending LIB stitch after the user authenticates. Runs the bridge so
 * the carried `lsn_aid` ($device_id) merges into the now-known uid ($user_id).
 * Safe to call repeatedly: clears the pending record once handled.
 */
export const flushPendingLibStitch = async () => {
  if (!isRudderStackAllowed()) {
    return;
  }

  const uid = auth.currentUser?.uid;
  if (!uid) {
    return;
  }

  try {
    const pending = await readPendingStitch();
    if (!pending) {
      return;
    }

    const prev = await getAnonymousId();
    try {
      await setAnonymousId(pending.lsn_aid);
      await trackStitchEvent(pending.lsn_aid, pending.source, uid);
    } finally {
      // Restore the app's anon id even if the track throws, so a failed flush
      // can't strand future events on the web lsn_aid. Pending is left in place
      // (clear only runs on success) so it retries on the next login.
      if (prev) {
        await setAnonymousId(prev);
      }
    }
    await clearPendingStitch();
  } catch (error) {
    captureException(error, {
      channel: "breadcrumb",
      area: "analytics",
      operation: "rudderstack-flush-pending-lib-stitch",
    });
  }
};
